// @ts-check
import { captureMessage, withScope } from '@sentry/react';

/**
 * Read MercadoPago's optional browser fingerprint without inventing a fallback value.
 *
 * @returns {string|undefined} The MercadoPago device-session identifier, when available.
 */
function getMercadoPagoDeviceSessionId() {
  if (typeof window === 'undefined') return undefined;

  const mercadoPagoWindow = /** @type {Window & {MP_DEVICE_SESSION_ID?: unknown}} */ (window);
  const deviceSessionId = mercadoPagoWindow.MP_DEVICE_SESSION_ID;
  if (typeof deviceSessionId !== 'string') return undefined;

  const normalizedDeviceSessionId = deviceSessionId.trim();
  return normalizedDeviceSessionId || undefined;
}

/**
 * Apply the MercadoPago-specific antifraud context to a Basis Theory payment payload.
 * The generic Funnel session is not a substitute for MercadoPago's device session.
 *
 * @param {object} paymentPayload - Payment payload built from the shared card contract.
 * @returns {object} A payload with the optional MercadoPago device-session identifier.
 */
function applyMercadoPagoDeviceSession(paymentPayload) {
  const payload = { ...paymentPayload };
  delete payload.device_session_id;
  const deviceSessionId = getMercadoPagoDeviceSessionId();

  if (!deviceSessionId) {
    try {
      withScope((scope) => {
        scope.clear();
        scope.addEventProcessor((event) => ({
          event_id: event.event_id,
          timestamp: event.timestamp,
          level: 'warning',
          message: 'mercadopago_bt_device_session_missing',
          tags: { payment_engine: 'mercadopago', checkout_mode: 'basis_theory' },
        }));
        captureMessage('mercadopago_bt_device_session_missing', 'warning');
      });
    } catch {
      // Optional antifraud diagnostics must never prevent payment creation.
    }
  }
  return deviceSessionId ? { ...payload, device_session_id: deviceSessionId } : payload;
}

export { applyMercadoPagoDeviceSession, getMercadoPagoDeviceSessionId };
